import React, { useCallback, useMemo, useState } from "react";
import { range } from "../@utils/range";
import { PaginationProps } from "./Pagination";

export const usePagination = (
  props:
    | {
        initialState?: {
          pageIndex?: number;
          pageSize?: number;
        };
        onChangePage?: PaginationProps["onChangePage"];
      }
    | undefined = {},
) => {
  const { onChangePage, initialState } = props;
  const [pageIndex, setPageIndex] = useState(initialState?.pageIndex ?? 0);
  const [pageSize, setPageSize] = useState(initialState?.pageSize ?? 5);
  const items = useMemo(
    () =>
      range(0, pageSize).map((index) => {
        return {
          getProps: () => {
            return {
              key: `pagination.item.${index}`,
              current: index === pageIndex,
              onClick: (
                event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
              ) => {
                setPageIndex(index);
                if (onChangePage) {
                  onChangePage(event, index);
                }
              },
            };
          },
          render: () => index + 1,
        };
      }),
    [onChangePage, pageIndex, pageSize],
  );
  const canPrevPage = pageIndex > 0;
  const canNextPage = pageIndex < pageSize - 1;
  const nextPage = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (canNextPage) {
        setPageIndex((prev) => {
          const next = prev + 1;
          if (onChangePage && event) {
            onChangePage(event, next);
          }
          return next;
        });
      }
    },
    [canNextPage, onChangePage],
  );
  const prevPage = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (canPrevPage) {
        setPageIndex((prev) => {
          const next = prev - 1;
          if (onChangePage && event) {
            onChangePage(event, next);
          }
          return next;
        });
      }
    },
    [canPrevPage, onChangePage],
  );
  const prevItem = useMemo(() => {
    return {
      getProps: () => ({
        disabled: !canPrevPage,
        onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          prevPage(event);
        },
      }),
    };
  }, [canPrevPage, prevPage]);
  const nextItem = useMemo(() => {
    return {
      getProps: () => ({
        disabled: !canNextPage,
        onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          nextPage(event);
        },
      }),
    };
  }, [canNextPage, nextPage]);
  return {
    items,
    prevItem,
    nextItem,
    setPageIndex,
    setPageSize,
    nextPage,
    prevPage,
    canPrevPage,
    canNextPage,
    pageIndex,
    pageSize,
  } as const;
};
